import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container, Typography, Button,
  CircularProgress, Snackbar, Alert, Box
} from '@mui/material';
import {
  Refresh,
  Add as AddIcon
} from '@mui/icons-material';
import {
  appointmentAPI, patientAPI, doctorAPI, userAPI,
  branchAPI, scanAPI, doctorSpecializationAPI, scanCategoryAPI
} from '../../services/api';
import { representativeService } from '../../services/representativeService';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRTL } from '../../hooks/useRTL';
import NoPrivilege from '../../components/common/NoPrivilege';
import { technicianAPI } from '../../services/api';
import AppointmentForm from './addAppointments';
import { AppointmentsTable } from './appointmentsTable';
import FilterBar from "./filterBar"
import AppointmentsStats from '../../components/appointments/appointmentsStats';
import DailyFinancialsCard from '../../components/dashboard/DailyFinancialsCard';
import { Print } from '@mui/icons-material';

// User types that don't require branch assignment
const BRANCH_EXEMPT_USER_TYPES = ['superAdmin', 'radiologist', 'representative'];

export default function Appointments({ daily = false }) {
  const { t } = useTranslation();
  const { isRTL, cardGridProps, iconContainerProps, textContainerProps, containerProps } = useRTL();

  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [radiologists, setRadiologists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [scans, setScans] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [scanCategories, setScanCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [totals, setTotals] = useState({});
  const [mainError, setMainError] = useState('');
  const [showDailyOnly, setShowDailyOnly] = useState(daily);

  // Updated filter state with plural names
  const [filters, setFilters] = useState({
    status: [],
    branchIds: [],
    startDate: null,
    endDate: null,
    patientIds: [],
    doctorIds: [],
    representativeIds: [],
    radiologistIds: [],
    scanIds: [],
    doctorSpecializationIds: [],
    scanCategoryIds: [],
    technicianIds: [],
    isPaid: null
  });

  const [financials, setFinancials] = useState({ income: 0, expenses: 0, net: 0, loading: false });
  const [pagination, setPagination] = useState({ page: 0, limit: 10, total: 0 });

  // Track if initial data has been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Ref to track if this is the first render
  const isFirstRender = useRef(true);

  // Debounce timer ref
  const debounceTimerRef = useRef(null);

  const {
    user, isSuperAdmin, canAdd, canEdit, canDelete, hasViewPrivilege,
    canAccessRadiologists, canAccessPatients, canAccessDoctors, canAccessBranches,
    canAccessRepresentatives, canAccessTechnicians, loading: authLoading,
    canAccessScanCategories, canAccessDoctorSpecialization
  } = useAuth();

  // Check if user is exempt from branch requirement
  const isBranchExempt = useMemo(() => {
    return isSuperAdmin || BRANCH_EXEMPT_USER_TYPES.includes(user?.userType);
  }, [isSuperAdmin, user?.userType]);

  // Check if user is a representative
  const isRepresentative = useMemo(() => {
    return user?.userType === 'representative';
  }, [user?.userType]);

  // Check if user is a radiologist
  const isRadiologist = useMemo(() => {
    return user?.userType === 'radiologist';
  }, [user?.userType]);

  // Fetch static data only once (patients, doctors, branches, etc.)
  const fetchStaticData = useCallback(async () => {
    try {
      const [
        scansRes,
        branchRes,
        doctorsRes,
        patientsRes,
        radiologistsRes,
        representativesRes,
        techniciansRes,
        specializationsRes,
        scanCategoriesRes,
      ] = await Promise.all([
        scanAPI.getAll({ limit: 50 }),
        canAccessBranches ? branchAPI.getAll({ limit: 50 }) : Promise.resolve(null),
        canAccessDoctors ? doctorAPI.getAll({ limit: 50 }) : Promise.resolve(null),
        canAccessPatients ? patientAPI.getAll({ limit: 50 }) : Promise.resolve(null),
        canAccessRadiologists && user?.userType !== 'radiologist'
          ? userAPI.getRadiologists({ limit: 50 })
          : Promise.resolve(null),
        canAccessRepresentatives
          ? representativeService.getAllRepresentatives({ limit: 50 })
          : Promise.resolve(null),
        canAccessTechnicians ? technicianAPI.getAll({ limit: 50 }) : Promise.resolve(null),
        doctorSpecializationAPI.getAll({ limit: 50 })
        ,
        canAccessScanCategories
          ? scanCategoryAPI.getAll({ limit: 50 })
          : Promise.resolve(null),
      ]);
      console.log("specializationsRes", specializationsRes);
      setScans(scansRes.data?.data || []);
      if (branchRes) setBranches(branchRes.data?.data || []);
      if (doctorsRes) setDoctors(doctorsRes.data?.data || []);
      if (patientsRes) setPatients(patientsRes.data?.data || []);
      if (radiologistsRes) setRadiologists(radiologistsRes.data?.data.filter(r => r.isActive) || []);
      if (representativesRes) setRepresentatives(representativesRes.data || []);
      if (techniciansRes) setTechnicians(techniciansRes.data?.data || []);
      if (specializationsRes) setSpecializations(specializationsRes.data?.data || []);
      if (scanCategoriesRes) setScanCategories(scanCategoriesRes.data?.data || []);

      setInitialDataLoaded(true);
    } catch (err) {
      console.error("fetchStaticData error:", err);
      setSnackbar({
        open: true,
        message: t('appointments.fetchError'),
        severity: 'error'
      });
    }
  }, [
    canAccessBranches,
    canAccessDoctors,
    canAccessPatients,
    canAccessRadiologists,
    canAccessRepresentatives,
    canAccessTechnicians,
    canAccessDoctorSpecialization,
    canAccessScanCategories,
    user?.userType,
    t
  ]);

  // Fetch appointments data (called when filters/pagination change)
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Build base params with plural filter names
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && filters.status.length > 0 && { status: filters.status }),
        ...(filters.branchIds && filters.branchIds.length > 0 && { branchIds: filters.branchIds }),
        ...(filters.patientIds && filters.patientIds.length > 0 && { patientIds: filters.patientIds }),
        ...(filters.doctorIds && filters.doctorIds.length > 0 && { doctorIds: filters.doctorIds }),
        ...(filters.representativeIds && filters.representativeIds.length > 0 && { representativeIds: filters.representativeIds }),
        ...(filters.radiologistIds && filters.radiologistIds.length > 0 && { radiologistIds: filters.radiologistIds }),
        ...(filters.scanIds && filters.scanIds.length > 0 && { scanIds: filters.scanIds.join(',') }),
        ...(filters.doctorSpecializationIds && filters.doctorSpecializationIds.length > 0 && { doctorSpecializationIds: filters.doctorSpecializationIds }),
        ...(filters.scanCategoryIds && filters.scanCategoryIds.length > 0 && { scanCategoryIds: filters.scanCategoryIds }),
        ...(filters.technicianIds && filters.technicianIds.length > 0 && { technicianIds: filters.technicianIds }),
        ...(filters.isPaid !== null && filters.isPaid !== undefined && { isPaid: filters.isPaid }),
      };

      // Handle dates AFTER building base params
      if (daily === true) {
        if (filters.startDate) {
          const userStartDate = new Date(filters.startDate);
          userStartDate.setHours(0, 0, 0, 0);
          params.startDate = userStartDate.toISOString();
        } else {
          params.startDate = today.toISOString();
        }

        if (filters.endDate) {
          const userEndDate = new Date(filters.endDate);
          userEndDate.setHours(23, 59, 59, 999);
          const currentStartDate = new Date(params.startDate);
          if (userEndDate >= currentStartDate) {
            params.endDate = userEndDate.toISOString();
          }
        }
      } else {
        if (filters.startDate) {
          const userStartDate = new Date(filters.startDate);
          userStartDate.setHours(0, 0, 0, 0);
          params.startDate = userStartDate.toISOString();
        }

        if (filters.endDate) {
          const userEndDate = new Date(filters.endDate);
          userEndDate.setHours(23, 59, 59, 999);

          if (params.startDate) {
            const currentStartDate = new Date(params.startDate);
            if (userEndDate >= currentStartDate) {
              params.endDate = userEndDate.toISOString();
            }
          } else {
            params.endDate = userEndDate.toISOString();
          }
        }
      }

      // Handle radiologist user type
      if (user?.userType === 'radiologist') {
        params.radiologistIds = [user._id];
      }

      // Build financials params
      const financialsParams = {
        ...(filters.branchIds && filters.branchIds.length > 0 && { branchIds: filters.branchIds }),
        ...(filters.patientIds && filters.patientIds.length > 0 && { patientIds: filters.patientIds }),
        ...(filters.doctorIds && filters.doctorIds.length > 0 && { doctorIds: filters.doctorIds }),
        ...(filters.representativeIds && filters.representativeIds.length > 0 && { representativeIds: filters.representativeIds }),
        ...(filters.radiologistIds && filters.radiologistIds.length > 0 && { radiologistIds: filters.radiologistIds }),
        ...(filters.scanIds && filters.scanIds.length > 0 && { scanIds: filters.scanIds.join(',') }),
        ...(filters.doctorSpecializationIds && filters.doctorSpecializationIds.length > 0 && { doctorSpecializationIds: filters.doctorSpecializationIds }),
        ...(filters.scanCategoryIds && filters.scanCategoryIds.length > 0 && { scanCategoryIds: filters.scanCategoryIds }),
        ...(filters.technicianIds && filters.technicianIds.length > 0 && { technicianIds: filters.technicianIds }),
        ...(filters.status && filters.status.length > 0 && { status: filters.status }),
        ...(filters.isPaid !== null && filters.isPaid !== undefined && { isPaid: filters.isPaid }),
      };

      // Handle dates for financials
      if (daily === true) {
        if (filters.startDate) {
          const userStartDate = new Date(filters.startDate);
          userStartDate.setHours(0, 0, 0, 0);
          financialsParams.startDate = userStartDate.toISOString();
        } else {
          financialsParams.startDate = today.toISOString();
        }

        if (filters.endDate) {
          const userEndDate = new Date(filters.endDate);
          userEndDate.setHours(23, 59, 59, 999);
          const currentStartDate = new Date(financialsParams.startDate);
          if (userEndDate >= currentStartDate) {
            financialsParams.endDate = userEndDate.toISOString();
          }
        } else {
          const endOfDay = new Date(financialsParams.startDate);
          endOfDay.setHours(23, 59, 59, 999);
          financialsParams.endDate = endOfDay.toISOString();
        }
      } else {
        if (filters.startDate) {
          const userStartDate = new Date(filters.startDate);
          userStartDate.setHours(0, 0, 0, 0);
          financialsParams.startDate = userStartDate.toISOString();
        }

        if (filters.endDate) {
          const userEndDate = new Date(filters.endDate);
          userEndDate.setHours(23, 59, 59, 999);

          if (financialsParams.startDate) {
            const currentStartDate = new Date(financialsParams.startDate);
            if (userEndDate >= currentStartDate) {
              financialsParams.endDate = userEndDate.toISOString();
            }
          } else {
            financialsParams.endDate = userEndDate.toISOString();
          }
        }
      }

      if (user?.userType === 'radiologist') {
        financialsParams.radiologistIds = [user._id];
      }

      // Representatives can see financials for their doctors
      const canSeeFinancials = isSuperAdmin || user?.userType === 'receptionist' || isRepresentative;

      const [aptRes, financialsRes] = await Promise.all([
        appointmentAPI.getAllAppointments(params),
        canSeeFinancials
          ? appointmentAPI.getFinancials(financialsParams)
          : Promise.resolve(null)
      ]);

      if (financialsRes) {
        setFinancials({
          ...financialsRes.data?.data,
          loading: false
        });
      }

      setAppointments(aptRes.data?.data || []);
      setTotals(aptRes.data?.totals || {});
      setPagination(prev => ({
        ...prev,
        total: aptRes.data?.pagination?.total || 0
      }));

    } catch (err) {
      const backendMessage = err.response?.data?.message;
      console.error("fetchAppointments error:", err);

      if (err.response?.status === 403) {
        setMainError(backendMessage);
      } else {
        setSnackbar({
          open: true,
          message: t('appointments.fetchError'),
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    filters,
    user,
    isSuperAdmin,
    isRepresentative,
    t,
    daily
  ]);

  // Combined fetch function for manual refresh
  const fetchData = useCallback(async () => {
    if (!initialDataLoaded) {
      await fetchStaticData();
    }
    await fetchAppointments();
  }, [initialDataLoaded, fetchStaticData, fetchAppointments]);

  const handleFormSubmit = async (appointmentData) => {
    try {
      const cleanedData = { ...appointmentData };
      if (typeof cleanedData.customPrice !== 'number' || isNaN(cleanedData.customPrice)) {
        delete cleanedData.customPrice;
        cleanedData.makeHugeSale = false;
      }
      if (selectedAppointment) {
        await appointmentAPI.updateAppointment(selectedAppointment._id, cleanedData);
        setSnackbar({ open: true, message: t('appointments.updateSuccess'), severity: 'success' });
      } else {
        await appointmentAPI.createAppointment(cleanedData);
        setSnackbar({ open: true, message: t('appointments.createSuccess'), severity: 'success' });
      }
      setOpenForm(false);
      setSelectedAppointment(null);
      fetchAppointments();

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || t('appointments.saveError'),
        severity: 'error',
      });
    }
  };

  const handleOpenNewAppointment = useCallback(() => {
    setSelectedAppointment(null);
    setOpenForm(true);
  }, []);

  const handleEditAppointment = useCallback((appointment) => {
    setSelectedAppointment(appointment);
    setOpenForm(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setOpenForm(false);
    setSelectedAppointment(null);
  }, []);

  // Initial load effect - fetch static data once
  useEffect(() => {
    if (authLoading) return;
    if (!hasViewPrivilege) {
      setMainError(t('appointments.noPrivilegeError'));
      setLoading(false);
      return;
    }

    // Check branch requirement only for non-exempt users
    if (!isBranchExempt && user && !user.branch) {
      setMainError(t('appointments.noBranchAssigned'));
      setLoading(false);
      return;
    }

    fetchStaticData();
  }, [authLoading, hasViewPrivilege, isBranchExempt, user, fetchStaticData, t]);

  // Effect to fetch appointments when filters or pagination change (with debounce for filters)
  useEffect(() => {
    if (!initialDataLoaded) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchAppointments();
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchAppointments();
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [initialDataLoaded, fetchAppointments]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setPagination(p => {
      if (p.page !== 0) {
        return { ...p, page: 0 };
      }
      return p;
    });
  }, [filters, daily]);

  const handleChangePage = useCallback((newPage) => {
    setPagination(p => ({ ...p, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newLimit) => {
    setPagination(p => ({
      ...p,
      limit: parseInt(newLimit, 10),
      page: 0
    }));
  }, []);

  const handleDeleteAppointment = useCallback(async (appointmentId) => {
    try {
      await appointmentAPI.deleteAppointment(appointmentId);
      setSnackbar({ open: true, message: t('appointments.deleteSuccess'), severity: 'success' });
      fetchAppointments();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || t('appointments.deleteError'),
        severity: 'error'
      });
    }
  }, [t, fetchAppointments]);

  const stats = useMemo(() => ({
    total: totals.totalAppointments || 0,
    completed: appointments.filter(apt => apt.status === 'completed').length,
    pending: appointments.filter(apt => apt.status === 'scheduled').length,
    cancelled: appointments.filter(apt => apt.status === 'cancelled').length,
    Income: user?.userType !== 'radiologist' ? (totals.totalIncome || 0) : 0,
    PaidIncome: user?.userType !== 'radiologist' ? (totals.totalPaidIncome || 0) : 0,
    upcoming: totals.upcomingAppointments || 0,
  }), [appointments, totals, user?.userType]);

  const toggleDailyAppointments = useCallback(() => {
    if (showDailyOnly) {
      setFilters(f => ({
        ...f,
        startDate: null,
        endDate: null,
      }));
      setShowDailyOnly(false);
    } else {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      setFilters(f => ({
        ...f,
        startDate: todayStart.toISOString(),
        endDate: todayEnd.toISOString(),
      }));
      setShowDailyOnly(true);
    }
  }, [showDailyOnly]);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(prev => {
      const hasChanged = JSON.stringify(prev) !== JSON.stringify(newFilters);
      if (hasChanged) {
        return typeof newFilters === 'function' ? newFilters(prev) : newFilters;
      }
      return prev;
    });
  }, []);

  // Show FilterBar for superAdmin, representative, and radiologist
  const showFilterBar = isSuperAdmin || isRepresentative || isRadiologist;

  if (authLoading || (loading && appointments.length === 0 && !initialDataLoaded)) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  if (!hasViewPrivilege) {
    return <NoPrivilege message={t('appointments.noPrivilegeError')} />;
  }

  if (mainError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>{mainError}</Alert>
      </Container>
    );
  }

  if (!isBranchExempt && user && !user.branch) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>{t('appointments.branchAssignmentError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ ...containerProps, py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenNewAppointment}
            >
              {t('appointments.add', 'New Appointment')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            {t('common.refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            className="no-print"
          >
            {t('common.print', 'Print')}
          </Button>
        </Box>
        <Typography variant="h4">
          {daily ? t('appointments.dailyTitle') : t('appointments.title')}
        </Typography>
      </Box>

      {daily && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('appointments.showingDailyAppointments')}: {new Date().toLocaleDateString()}
          </Typography>
        </Alert>
      )}

      {user.userType !== 'representative' && (
        <AppointmentsStats
          stats={stats}
          user={user}
          cardGridProps={cardGridProps}
          iconContainerProps={iconContainerProps}
          textContainerProps={textContainerProps}
          t={t}
          daily={daily}
        />
      )}

      {showFilterBar && (
        <FilterBar
          filters={filters}
          setFilters={handleFiltersChange}
          user={user}
          initialPatients={patients}
          initialDoctors={doctors}
          initialRadiologists={radiologists}
          scans={scans}
          representatives={representatives}
          scanCategories={scanCategories}
          technicians={technicians}
          specializations={specializations}
          allowedBranches={branches}
          doctorSpecializations={specializations}
          // superAdmin can select any branch, radiologist can also select branch
          canSelectBranch={isSuperAdmin || isRadiologist || (user?.userType === 'representative')}
        />
      )}

      <Button
        onClick={toggleDailyAppointments}
        variant="outlined"
        sx={{ mt: 2, mb: 2 }}
      >
        {showDailyOnly ? t('appointments.dailyTitle') : t('appointments.title')}
      </Button>

      <AppointmentsTable
        appointments={appointments}
        isRTL={isRTL}
        canEdit={canEdit}
        canDelete={canDelete}
        fetchData={fetchAppointments}
        onEdit={handleEditAppointment}
        onDelete={handleDeleteAppointment}
        t={t}
        scans={scans}
        user={user}
        totals={totals}
        filters={filters}
        daily={daily}
        patients={patients}
        doctors={doctors}
        radiologists={radiologists}
        technicians={technicians}
        setPatients={setPatients}
        branches={branches}
        pagination={pagination}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        loading={loading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {canAdd && (
        <AppointmentForm
          open={openForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          initialData={selectedAppointment}
          patients={patients}
          doctors={doctors}
          radiologists={radiologists}
          branches={branches}
          scans={scans}
          technicians={technicians}
          setPatients={setPatients}
        />
      )}

      {(isSuperAdmin || user?.userType === 'receptionist') && (
        <DailyFinancialsCard financials={financials} loading={loading} />
      )}
    </Container>
  );
}